import React, { useState, useEffect } from "react";

const BMICalculator = () => {
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(170);
  const [gender, setGender] = useState("male");
  const [unitSystem, setUnitSystem] = useState("metric");
  const [age, setAge] = useState(25);
  const [result, setResult] = useState({
    bmiValue: "0.00",
    category: "",
    interpretation: "",
    idealWeightRange: "",
  });

  // BMI Calculation
  const calculateBMI = () => {
    let w = parseFloat(weight);
    let h = parseFloat(height);

    if (isNaN(w) || w <= 0 || isNaN(h) || h <= 0) return;

    let bmi;
    if (unitSystem === "metric") {
      bmi = w / Math.pow(h / 100, 2);
    } else {
      bmi = (w / Math.pow(h, 2)) * 703;
    }

    let category = "";
    let interpretation = "";
    if (bmi < 18.5) {
      category = "Underweight";
      interpretation =
        "Your BMI is below the normal range. Consider a balanced diet and consult a health professional if needed.";
    } else if (bmi >= 18.5 && bmi < 24.9) {
      category = "Normal Weight";
      interpretation =
        "Your BMI is within the normal range. Maintain a balanced diet and regular exercise to stay healthy.";
    } else if (bmi >= 25 && bmi < 29.9) {
      category = "Overweight";
      interpretation =
        "Your BMI indicates that you're slightly above the healthy range. Regular exercise and mindful eating can help.";
    } else {
      category = "Obese";
      interpretation =
        "Your BMI is in the obese category. It is advisable to consult a doctor or nutritionist for guidance.";
    }

    // Ideal weight range
    const minWeight = (18.5 * Math.pow(h / 100, 2)).toFixed(1);
    const maxWeight = (24.9 * Math.pow(h / 100, 2)).toFixed(1);
    const idealWeightRange = `${minWeight}kg - ${maxWeight}kg`;

    setResult({
      bmiValue: bmi.toFixed(2),
      category,
      interpretation,
      idealWeightRange,
    });
  };

  useEffect(() => {
    calculateBMI();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
          </div>
          <h1>BMI Calculator</h1>
          <p>
            Calculate your Body Mass Index (BMI) to understand your health status and find your ideal weight range based on height and gender.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                BMI Inputs
              </h2>

              {/* Weight Input */}
              <div className="input-group">
                <label>Weight ({unitSystem === "metric" ? "kg" : "lbs"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">⚖️</span>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="calc-input"
                    placeholder="70"
                  />
                </div>
                <div className="quick-select-group">
                  {[50, 60, 70, 80, 90].map((val) => (
                    <button
                      key={val}
                      onClick={() => setWeight(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Height Input */}
              <div className="input-group">
                <label>Height ({unitSystem === "metric" ? "cm" : "in"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📏</span>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="calc-input"
                    placeholder="170"
                  />
                </div>
                <div className="quick-select-group">
                  {[150, 160, 170, 180, 190].map((h) => (
                    <button
                      key={h}
                      onClick={() => setHeight(h)}
                      className="quick-select-btn success"
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gender */}
              <div className="input-group">
                <label>Gender</label>
                <div className="quick-select-group">
                  {["male", "female", "other"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      className={`quick-select-btn ${gender === g ? "primary" : "secondary"}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age */}
              <div className="input-group">
                <label>Age</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎂</span>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="1"
                  />
                </div>
              </div>

              {/* Unit System */}
              <div className="input-group">
                <label>Measurement System</label>
                <div className="quick-select-group">
                  {[
                    { value: "metric", label: "Metric (kg/cm)" },
                    { value: "imperial", label: "Imperial (lbs/in)" },
                  ].map((unit) => (
                    <button
                      key={unit.value}
                      onClick={() => setUnitSystem(unit.value)}
                      className={`quick-select-btn ${unitSystem === unit.value ? "primary" : "secondary"}`}
                    >
                      {unit.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateBMI} className="calculate-btn">
                Calculate BMI
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                BMI Result
              </h2>

              <div className="result-card">
                {/* BMI Value */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Your BMI</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 8v4l3 2"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.bmiValue}</div>
                  <div className="result-subtext">{result.category}</div>
                </div>

                {/* Ideal Weight Range */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Ideal Weight Range</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.idealWeightRange}</div>
                  <div className="result-subtext">Healthy range based on height</div>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Interpretation</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.interpretation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding BMI</h3>
              <p>
                Body Mass Index (BMI) is a measure that uses your height and weight to estimate body fat levels. It helps identify whether you’re underweight, at a healthy weight, overweight, or obese. 
                While BMI is a good general indicator, it does not account for muscle mass, bone density, or body composition. For athletes or muscular individuals, BMI may overestimate body fat.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>BMI Calculation Formula:</h4>
            <div className="formula-display">
              BMI = Weight (kg) / (Height (m))²
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Metric Formula</div>
                <div className="formula-param-value">BMI = kg / (m²)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Imperial Formula</div>
                <div className="formula-param-value">BMI = (lbs × 703) / (in²)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Normal Range</div>
                <div className="formula-param-value">18.5 – 24.9</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a healthy BMI range?</h5>
              <p>
                A BMI between 18.5 and 24.9 is considered normal for most adults.
              </p>
            </div>

            <div className="faq-item">
              <h5>Does BMI apply to everyone?</h5>
              <p>
                BMI is not always accurate for athletes, children, pregnant women, or older adults as it doesn’t distinguish between fat and muscle.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if my BMI is high?</h5>
              <p>
                High BMI indicates potential overweight or obesity. Consult a healthcare professional for dietary and exercise recommendations.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I lower my BMI?</h5>
              <p>
                Balanced nutrition, regular physical activity, and healthy sleep patterns are effective ways to reduce BMI over time.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can BMI be too low?</h5>
              <p>
                Yes, a BMI below 18.5 indicates underweight, which may lead to fatigue, immune deficiency, and other health concerns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BMICalculator;
